import { Injectable } from '@angular/core';

import { Subject, Subscription } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';

/**
 * Max wait for a correlated local-WS response.
 * Above keep-alive ping latency (5s) so large print payloads can complete;
 * below a hung await that blocks the caller indefinitely.
 */
export const WSL_SEND_TIMEOUT_MS = 30000;

import { AppConstants } from '@unpispas/upp-defs';
import { GenericUtils } from '@unpispas/upp-defs';

import { httpService } from './http';
import { adhocService } from './adhoc';

/**
 * Defines the possible WebSocket connection types.
 */
export type WsConnType = 'WSR' | 'WSL';

/*****************************/
/* ENABLE VERBOSE LOGGING    */
/*****************************/

/**
 * @internal
 */
class CustomLogger {
    private _verbose = false;
    addinfo(message: string){
        if (this._verbose){
            console.info(message);
        }
    }

    addwarn(message: string){
        if (this._verbose){
            console.warn(message);
        }
    }
}

/*****************************/
/* SERVICE ABSTRACT CLASS    */
/*****************************/

/**
 * @internal
 */
abstract class _Service extends CustomLogger {    
    protected host: string | null = null;
    protected port: number | null  = null;
    
    abstract Connect(host: string | null, port: number | null, name: string | null) : Promise <WSConnection | null>;
} 

/*****************************/
/* CONNECTION ABSTRACT CLASS */
/*****************************/

/**
 * Abstract base class for managing WebSocket connections.
 * This class provides a framework for establishing and managing WebSocket connections,
 * with built-in support for event handling and logging capabilities.
 */
export abstract class WSConnection extends CustomLogger {
    protected _service: _Service | null = null; 

    /**
     * @description Gets or sets the associated service for this WebSocket connection.
     * @type {_Service | null}
     */    
    get service() : _Service | null {
        return this._service;
    }

    set service(value: _Service | null){
        this._service = value;;
    }
    
    protected _version: string | null = null;   
    
    /**
     * @description Gets or sets the WebSocket connection version.
     * @type {string | null}
     */    
    get version(): string | null {
        return this._version;
    }

    set version(value: string | null){
        this._version = value;
    }
    
    protected _svcname: string | null = null;

    /**
     * @description Gets or sets the service name for this WebSocket connection.
     * @type {string | null}
     */    
    get svcname(): string | null {
        return this._svcname;
    }

    set svcname(value: string | null){
        this._svcname = value;
    }

    private _contype: WsConnType | null = null;

    /**
     * @description Gets the WebSocket connection type.
     * This property is read-only and indicates whether the connection is local or remote.
     * @type {WsConnType | null}
     */    
    get contype(): WsConnType | null {
        return this._contype;
    }

    protected _onDisconnected = new Subject<void> ();  
    /** Emits when the WebSocket connection is disconnected. */    
    public OnDisconnected = this._onDisconnected.asObservable(); 

    protected _onMessageSent = new Subject<any> ();  
    /** Emits when a message is sent via the WebSocket connection. */    
    public OnMessageSent = this._onMessageSent.asObservable();  

    protected _onMessageRecv = new Subject<any> ();    
    /** Emits when a message is received via the WebSocket connection. */    
    public OnMessageRecv = this._onMessageRecv.asObservable();  

    /**
     * Constructs a new `WSConnection` instance.
     * Initializes the connection with the specified type and service name.
     * @param type The WebSocket connection type (e.g., 'WSR' for remote or 'WSL' for local).
     * @param name The name of the service associated with this WebSocket connection.
     */    
    constructor(type: WsConnType, name: string){
        super();

        this._svcname = name;
        this._contype = type;
    }    

    /**
     * Disconnects the WebSocket connection and emits the disconnect event.
     */    
    Disconnect(){
        this._onDisconnected.next();
    }

    /**
     * Sends a message through the WebSocket connection.
     * @param module The target module.
     * @param message The message to send.
     * @returns A promise resolving to any response from the server.
     */    
    abstract Send(module: string, message: any): Promise <any>;
}

/*****************************/
/* INPUT ABSTRACT CLASS      */
/*****************************/

/**
 * Abstract base class for WebSocket input handling.
 * 
 * `WSInput` provides a structure for reading data from a WebSocket connection. It includes
 * mechanisms for subscribing to WebSocket messages, managing connection states, and notifying
 * when data is received or lost.
 * 
 * This class is meant to be extended by other classes that will define specific read and end
 * read behaviors through the `doIniRead` and `doEndRead` methods.
 */
export abstract class WSInput {
    private _connection: WSConnection | null = null; 
    private _target: string | null = null;
    private _inputid: string | null = null;

    private _read_subscription: Subscription | null = null;
    private _conn_subscription: Subscription | null = null;

    protected _onInputRead = new Subject<string> ();   
    /** Observable that emits data read from the WebSocket input. */ 
    public OnInputRead = this._onInputRead.asObservable();  
    protected _onInputLost = new Subject<void> ();    
    /** Observable that emits when the WebSocket input is lost or disconnected. */
    public OnInputLost = this._onInputLost.asObservable();  

    /**
     * Creates an instance of `WSInput` and initializes the WebSocket connection.
     * @param connection The WebSocket connection to be used.
     * @param target The target module or service for the WebSocket connection.
     */
    constructor(connection: WSConnection, target: string){
        if (connection.contype != 'WSL'){
            console.error("[" + connection.contype + "] cannot read from remote websocket connection")
        }
        else {
            this._connection = connection;
            this._conn_subscription = connection.OnDisconnected.subscribe(
            () => {
                this._onInputLost.next();
            });

            this._target = target;    
        }
    }  

    /**
     * Starts reading data from the WebSocket input.
     * To be implemented by subclasses with specific read initialization logic.
     * @param args The arguments required to initialize the read operation.
     * @returns A promise that resolves when the read operation is initiated.
     */
    abstract doIniRead(args: any): Promise <any>;

    /**
     * Ends the read operation on the WebSocket input.
     * To be implemented by subclasses with specific read termination logic.
     * @returns A promise that resolves when the read operation is completed.
     */
    abstract doEndRead(): Promise <any>;

    /**
     * Ends capture and unsubscribes without disconnecting the socket.
     * Caller owns `WSConnection` lifecycle (`WSClose` / `Disconnect`).
     */
    async Release(): Promise<void> {
        await this.doEndRead();

        if (this._read_subscription){
            this._read_subscription.unsubscribe();
            this._read_subscription = null;
        }

        if (this._conn_subscription){
            this._conn_subscription.unsubscribe();
            this._conn_subscription = null;
        }
    }

    /**
     * Ends capture, unsubscribes, and disconnects the underlying socket.
     */
    async OnDestroy(){
        await this.Release();

        if (this._connection){
            this._connection.Disconnect();
            this._connection = null;
        }
    }

    /***************************/
    /* PROTECTED METHODS       */
    /***************************/

    protected async _doIniRead(_action: any): Promise <any> {
        if (!this._connection || !this._target){
            return null;    // not connected
        }

        const _response = await this._connection.Send(this._target, _action);
        if (_response && _response.exec == 'SUCCESS'){
            this._inputid = _response.data;

            console.info("[" + this._connection.contype + "] starting read on target: '" + this._target + "' with session (" + this._inputid + ")");
            if (this._read_subscription){
                this._read_subscription.unsubscribe();
                this._read_subscription = null;
            }

            this._read_subscription = this._connection.OnMessageRecv.subscribe(
            data => {
                if (data['uuid'] == this._inputid){
                    this._onInputRead.next(data['data']);    
                }
            });

            return _response;
        }
        else {
            console.error("[" + this._connection.contype + "] read request to target '" + this._target + "' failed! (" + (_response ? _response.data : 'no response') + ")")
            return null;
        }
    }

    protected async _doEndRead(_action: any): Promise <any>  {
        if (this._read_subscription){
            this._read_subscription.unsubscribe();
            this._read_subscription = null;
        }

        if (!this._connection || !this._target){
            return null;    // not connected
        }

        const _response = await this._connection.Send(this._target, _action);
        if (_response && _response.exec == 'SUCCESS'){
            console.info("[" + this._connection.contype + "] stop request to target: '" + this._target + "'");
            return _response;
        }
        else {
            console.error("[" + this._connection.contype + "] stop request to target '" + this._target + "' failed! (" + (_response ? _response.data : 'no response') + ")")
            return null;    
        }
    }
}

/**
 * @internal
 */
class InputScale extends WSInput {
    private _args: Record<string, unknown> | null = null;

    constructor(connection: WSConnection, private device: any){
        super(connection, 'SCALE');
    }

    /**
     * Starts continuous weight capture. Payload always carries `device`/`port`
     * from the configured ScaleDevice (caller args may add optional fields).
     */
    async doIniRead(args: any): Promise <any> {
        if (!this.device){
            return null;    // no device provided 
        }

        this._args = {
            ...(args || {}),
            command: 'START',
            device: this.device.model,
            port: this.device.port || 'none'
        };

        return this._doIniRead(this._args);
    }

    async doEndRead(): Promise <any> {
        if (!this.device){
            return null;    // no device provided 
        }

        this._args = {
            ...(this._args || {}),
            command: 'STOP',
            device: this.device.model,
            port: this.device.port || 'none'
        };

        return this._doEndRead(this._args);
    }
}

/*****************************/
/* LOCAL CACHE STORE         */
/*****************************/

/**
 * Key-value transport for the local `pispas-connect` `cache` module.
 *
 * Operates only on a local (`WSL`) connection. After the underlying socket
 * disconnects, further Get/Set/Del resolve to null/false without sending.
 * Caller owns the `WSConnection` lifecycle.
 */
export class LocalCacheStore {
    private _connection: WSConnection | null = null;
    private _conn_subscription: Subscription | null = null;
    private readonly _target = 'cache';

    /**
     * Binds to an open local connection. Remote connections leave the store inert.
     * @param connection Open WebSocket connection (`WSL` required).
     */
    constructor(connection: WSConnection) {
        if (connection.contype != 'WSL') {
            console.error('[' + connection.contype + '] cannot use cache store on remote websocket connection');
            return;
        }

        this._connection = connection;
        this._conn_subscription = connection.OnDisconnected.subscribe(() => {
            this._Invalidate();
        });
    }

    /**
     * Reads a string value for `key` from the local cache module.
     * @param key Cache key.
     * @returns Stored string, or null when missing, failed, or disconnected.
     */
    async Get(key: string): Promise<string | null> {
        if (!this._connection) {
            return null;
        }

        const _response = await this._connection.Send(this._target, {
            command: 'GET',
            key: key
        });

        if (!_response || _response.exec != 'SUCCESS' || _response.data == null) {
            return null;
        }

        // Missing key: current runtimes return JSON null; older pispas-connect
        // builds answered SUCCESS with the literal status string "Key not found".
        if (_response.data === 'Key not found') {
            return null;
        }

        return typeof _response.data === 'string' ? _response.data : String(_response.data);
    }

    /**
     * Writes a string value under `key` in the local cache module.
     * @param key Cache key.
     * @param value String payload to store.
     * @returns True when the runtime reports SUCCESS; false otherwise.
     */
    async Set(key: string, value: string): Promise<boolean> {
        if (!this._connection) {
            return false;
        }

        const _response = await this._connection.Send(this._target, {
            command: 'SET',
            key: key,
            value: value
        });

        return !!(_response && _response.exec == 'SUCCESS');
    }

    /**
     * Deletes `key` from the local cache module.
     * @param key Cache key.
     * @returns True when the runtime reports SUCCESS; false otherwise.
     */
    async Del(key: string): Promise<boolean> {
        if (!this._connection) {
            return false;
        }

        const _response = await this._connection.Send(this._target, {
            command: 'DEL',
            key: key
        });

        return !!(_response && _response.exec == 'SUCCESS');
    }

    private _Invalidate(): void {
        if (this._conn_subscription) {
            this._conn_subscription.unsubscribe();
            this._conn_subscription = null;
        }

        this._connection = null;
    }
}

/*****************************/
/* LOCAL WS CONNECTION       */
/*****************************/

/*
    The connection is performed from the browser to the application (websocket server) running in localhost
    The connection is capable od sending and receiving messages to and from this server
*/

/**
 * @internal
 */
class WSConnectionL extends WSConnection {
    private ws: WebSocket | null = null;

    constructor(name: string, _ws: WebSocket){
        super('WSL', name);    // this is temporal: will be overwritten by the ws server
        
        this.ws = _ws;
        _ws.onmessage = (event) => {
            const _message_json = JSON.parse(event.data)
            if (_message_json){
                this._onMessageRecv.next({
                    name: _message_json['SERVICE_NAME'],    // service identifier configured in the ws 
                    vers: _message_json['SERVICE_VERS'],    // service version configured in the ws
                    uuid: _message_json['MESSAGE_UUID'],    // message identifier (in case of response)
                    exec: _message_json['MESSAGE_EXEC'],    // execution result
                    data: _message_json['MESSAGE_DATA']     // message content (result or error message)
                });  
            } 
            else {
                this._onMessageRecv.next({
                    exec: 'SUCCESS',                        // execution result
                    data: event.data                        // response is not a JSON object
                });  
            }
        };

        _ws.onclose = () => {
            if (this.ws){   // null if we have closed the session
                console.warn("[" + this.contype + "] Websocket connection lost");
                
                this._stopKeepAlive();
                this._onDisconnected.next();    
            }
        };

        this._startKeepAlive();     // keep-alive ping every 20 seconds
    }

    override Disconnect(){
        if (this.ws){
            this.ws.close();
            this.ws = null;

            this.addinfo("[" + this.contype + "] Websocket connection closed");
        }

        this._stopKeepAlive();
        super.Disconnect();
    }
    
    Send(module: string, message: any): Promise <any> {
        return new Promise <any> ((resolve) => {
            if (!this.ws){
                resolve(null);     // not connected
                return;
            }

            if (this.ws.readyState !== WebSocket.OPEN){
                resolve(null);  // send error
                return;
            }

            const _uuidv4 = GenericUtils.uuidv4();
            const _sendmsg = {
                UUIDV4: _uuidv4,
                TARGET: module,
                ACTION: message
            };

            this.OnMessageRecv.pipe(
                filter(_response => !!_response && (_response['uuid'] == _uuidv4)),
                take(1),
                timeout(WSL_SEND_TIMEOUT_MS)
            ).subscribe({
                next: _response => {
                    if (_response.name != this.svcname){
                        console.warn("[" + this.contype + "] Local server configured for '" + _response.name + "' ('" + this.svcname + "' is expected)")
                    }

                    this._version = _response['vers'];
                    this._svcname = _response['name'];

                    resolve({
                        exec: _response['exec'],
                        data: _response['data']
                    });

                    if (_response['exec'] != 'SUCCESS'){
                        this.addwarn("[" + this.contype + "] " + _response['exec'] + " (" + _response['data'] + ")");
                    }
                },
                error: () => {
                    this.addwarn("[" + this.contype + "] FAILURE (timeout waiting for response)");
                    resolve({
                        exec: 'FAILURE',
                        data: 'timeout waiting for response'
                    });
                }
            });

            this.ws.send(JSON.stringify(_sendmsg));
            this._onMessageSent.next(_sendmsg);
        });
    }

    private _keepalive_interval: any | null = null;
    private _startKeepAlive() {
        if (this._keepalive_interval) {
            clearInterval(this._keepalive_interval);
            this._keepalive_interval = null;
        }

        this._keepalive_interval = setInterval(() => {
            const _timeout = setTimeout(() => {
                console.warn("[" + this.contype + "] ping request not satisfied after 5 seconds");
                this.Disconnect();
            }, 5000);

            this.Send("BASE", "PING").then(
            () => {
                clearTimeout(_timeout);
            })
        }, 20000);
    }

    private _stopKeepAlive() {
        if (this._keepalive_interval) {
            clearInterval(this._keepalive_interval);
            this._keepalive_interval = null;
        }
    }    
};

/**
 * @internal
 */
class LocalService extends _Service {
    Connect(host: string | null, port: number | null, name: string) : Promise <WSConnection | null> {
        this.host = host;
        this.port = port;

        const _url = "ws://" + this.host + ":" + this.port;
        return new Promise <WSConnection | null> ((resolve) => {
            if (!this.host || !this.port || !name){
                console.info("[WSL] Websocket connection error (not provided)");
                return resolve(null);
            }
    
            let _ws = null;
            try {
                _ws = new WebSocket(_url);
            }
            catch(error){
                console.error("[WSL] Cannot create websocket connection", error);
                return resolve(null);     // connection failed
            }

            _ws.onerror = () => {
                console.warn("[WSL] Websocket connection error");
                resolve(null);          // connection failed (could not open connection)
            };                   

            _ws.onopen = () => {
                const _connection = new WSConnectionL(name, _ws);
                if (_connection){
                    _connection.service = this;

                    _connection.OnDisconnected.pipe(take(1)).subscribe(
                    () => {
                        resolve(null);      // connection closed
                    });        

                    _connection.Send("BASE", "CHECK").then(
                    _response => {
                        if (_response && _response.exec == 'SUCCESS'){
                            console.info("[WSL] Websocket connection ready");
                            resolve(_connection);   // connected          
                        }
                        else {
                            resolve(null);      // connection failed (invalid check)
                        }
                    });
                }
            };
        });
    }
}

/*****************************/
/* REMOTE WS CONNECTION      */
/*****************************/

/*
    The connection is performed from the wsr REST server to the wss (websocket server) running hosted
    The local websocket server has registered to the remote wss server that will act as gateway
    Messages sent to the wsr REST server will be sent to the wss server and then back to the targeted web socket server
    Through this method, the connection is capable od sending single messages to the local running server 
*/

/**
 * @internal
 */
class WSConnectionR extends WSConnection {
    constructor(name: string, private http: httpService){
        super('WSR', name);
    }

    override Disconnect(){
        super.Disconnect();
    }
    
    async Send(module: string, message: any): Promise <any> {
        const _url = AppConstants.restURL + "send?service=" + this.svcname;

        if (!this.http.IsOnline){
            return null;
        }
    
        try {
            const response = await fetch(_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'text/plain',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ TARGET: module, ACTION: message })
            });                    

            if (response.ok) {
                const _data = await response.json();

                this._version = _data['vers']; 
                this._svcname = _data['name'];

                if (_data['exec'] != 'SUCCESS'){
                    this.addwarn("[" + this.contype + "] " + _data['exec'] + " (" + _data['data'] + ")");
                }                

                return {
                    exec: _data['exec'],
                    data: _data['data'] 
                }
            }
            else {
                return null;      // error on execution
            }        
        } 
        catch (error) {
            console.error("[WSR] Failed to fetch (" + _url + ")", error);
            return null;          // exception in fetch
        }            
    };
};

/**
 * @internal
 */
class RemoteService extends _Service {
    constructor(private http: httpService){
        super();
    }

    Connect(host: string | null, port: number | null, name: string) : Promise <WSConnection | null> {
        this.host = host;
        this.port = port;

        return new Promise <WSConnection | null> ((resolve) => {
            const _connection = new WSConnectionR(name, this.http);
            if (_connection){
                _connection.service = this;

                _connection.Send("BASE", "CHECK").then(
                _response => {
                    if (_response && _response.exec == 'SUCCESS'){
                        this.addinfo("[WSR] Websocket connection ready");
                        resolve(_connection);   // connected          
                    }
                    else {
                        resolve(null);          // connection failed (invalid check)
                    }
                });            
            }
        });
    }     
}

/*****************************/
/* DEVICES SERVICE           */
/*****************************/

/**
 * Service for managing local and remote WebSocket connections to various devices.
 * Provides methods for connecting to WebSocket services, checking availability, and controlling connected devices.
 */
@Injectable({
    providedIn: 'root' 
})
export class deviceService {
    private _localservice: LocalService | null = null;
    private get localService(){
        if (!this._localservice){
            this._localservice = new LocalService()
        }
        return this._localservice;
    }

    private _rmoteservice: RemoteService | null = null;
    private get rmoteService(){
        if (!this._rmoteservice){
            this._rmoteservice = new RemoteService(this.http)
        }
        return this._rmoteservice;
    }

    /****************************/
    /* STARTS HERE              */
    /****************************/

    /**
     * Constructs a new instance of the `deviceService` class.
     * @param http The HTTP service used for managing HTTP connections.
     * @param adhoc The ad hoc service for handling custom HTTP requests.
     */    
    constructor(private http: httpService, private adhoc: adhocService){
        // nothing to do
    }    

    /****************************/
    /* NAME AVAILABILITY        */
    /****************************/

    private _freeServiceUrl = 'sockets/available.php';

    /**
     * Checks if a specific service is available for the current place.
     * The place is sent automatically by the adhoc layer from the application state.
     * @param service The name of the service to check for availability.
     * @returns A promise that resolves to a boolean indicating if the service is available.
     */
    Available(service: string): Promise <boolean> {
        return new Promise((resolve) => {
            this.adhoc.DoRequest(this._freeServiceUrl, { service: service })
            .then(data => {
                resolve(data as boolean);
            }, error => {
                if (error.status != 0){
                    console.error("[REQUEST] Error [" + error.status + "] in http request '" + this._freeServiceUrl + "'");
                }

                resolve(false);
            }); 
        });
    }

    /****************************/
    /* CONNECT CONTROL          */
    /****************************/
        
    /**
     * Establishes a WebSocket connection to either a local or remote service.
     * @param host The host address for the connection.
     * @param port The port number for the connection.
     * @param service The name of the service to connect to.
     * @param tolocal Whether to connect to a local or remote service.
     * @returns A promise that resolves to the established `WSConnection`, or null if connection fails.
     */    
    Connect(host: string | null, port: number | null, service: string, tolocal: boolean): Promise <WSConnection | null> {
        if (tolocal){
            return this.localService.Connect(host, port, service);
        }
        else {
            return this.rmoteService.Connect(host, port, service);
        }
    }

    /**
     * Disconnects an existing WebSocket connection.
     * @param _connection The WebSocket connection to disconnect.
     */    
    Disconnect(_connection: WSConnection){
        if (_connection){
            _connection.Disconnect();
        }

        this._localservice = null;
        this._rmoteservice = null;
    }

    /****************************/
    /* CONNECTION CHECK         */
    /****************************/

    /**
     * Tests the connection to a WebSocket service and then disconnects.
     * @param host The host address for the connection.
     * @param port The port number for the connection.
     * @param service The name of the service to connect to.
     * @param tolocal Whether to test a local or remote service connection.
     * @returns A promise resolving to the established `WSConnection` if successful, or null otherwise.
     */    
    async Test(host: string | null, port: number | null, service: string, tolocal: boolean) : Promise <WSConnection | null> {
        let _success = null;

        if (tolocal){
            const _connection = await this.localService.Connect(host, port, service);
            if (_connection){
                this.Disconnect(_connection);
                _success = _connection;
            }
        }
        else {
            const _connection = await this.rmoteService.Connect(host, port, service);
            if (_connection){
                this.Disconnect(_connection);
                _success = _connection;
            }
        }

        return _success;
    }

    /****************************/
    /* PRINTER ACTIONS          */
    /****************************/

    /**
     * Sends an open command to a printing device through the WebSocket connection.
     * @param connection The WebSocket connection to use.
     * @param device The identifier of the printing device.
     * @returns A promise resolving to any response from the server, or logs errors to the console.
     */    
    async doOpen(connection: WSConnection, device: string){
        if (!connection || !connection.service){
            return;
        }

        console.info("[DRAWER] Opening cash drawer on printer '" + device + "'..");

        const _message: Record<string, any> = {};
        _message[device] = {
            print: null,
            copies: 0,
            open: true
        }
        
        const _response = await connection.Send("PRINT", JSON.stringify(_message));
        if (_response && _response.exec == 'SUCCESS'){
            console.info("[" + connection.contype + "] open request sent to device '" + device + "'")
        }
        else {
            console.error("[" + connection.contype + "] open request to device '" + device + "' failed! (" + (_response ? _response.data : 'no response') + ")")
        }

        return _response;
    }

    /**
     * Sends a print command with the specified HTML content to a printing device.
     * @param connection The WebSocket connection to use.
     * @param device The identifier of the printing device.
     * @param html The HTML content to print.
     * @param copies The number of copies to print. Defaults to 1.
     * @returns A promise resolving to any response from the server, or logs errors to the console.
     */    
    async doPrint(connection: WSConnection, device: string, html: string, copies = 1){
        if (!connection || !connection.service){
            return;
        }

        const _message: Record<string, any> = {};
        _message[device] = {
            print: GenericUtils.stringToBase64(html),
            copies: copies,
            open: false
        }

        const _response = await connection.Send("PRINT", JSON.stringify(_message));
        if (_response && _response.exec == 'SUCCESS'){
            console.info("[" + connection.contype + "] print request sent to device '" + device + "'")
        }
        else {
            console.error("[" + connection.contype + "] open request to device '" + device + "' failed! (" + (_response ? _response.data : 'no response') + ")")
        }

        return _response;    
    }

    /**
     * Asks the local print runtime for installed printer names (`ACTION: getPrinters`).
     * Resolves to a trimmed unique name list when `MESSAGE_DATA` is an array; otherwise [].
     * Useful only on a local (`WSL`) connection — remote gateway returns an ack string, not names.
     * @param connection Open WebSocket connection to the print service.
     * @returns Printer device names suitable for `PrintDevice.device`.
     */
    async Printers(connection: WSConnection): Promise<string[]> {
        if (!connection || !connection.service){
            return [];
        }

        const _response = await connection.Send("PRINT", "getPrinters");
        if (!_response || (_response.exec != 'SUCCESS') || !Array.isArray(_response.data)){
            return [];
        }

        const _names: string[] = [];
        for (const _item of _response.data){
            const _name = String(_item || '').trim();
            if (_name && !_names.includes(_name)){
                _names.push(_name);
            }
        }

        return _names;
    }

    /****************************/
    /* INPUT DEVICES            */
    /****************************/

    /**
     * Creates a scale reader on an already open `WSConnection`.
     * Does not open or close the socket; caller owns connection lifecycle.
     * @param connection Open WebSocket connection (`WSL` required for live capture).
     * @param device Scale identity (`model`, optional `port`) for START/STOP.
     * @returns `WSInput` reader, or null when connection/device are missing.
     */
    WSInputScale(connection: WSConnection | null, device: { model?: string | null; port?: string | null } | null): WSInput | null {
        if (!connection || !device?.model){
            return null;
        }

        return new InputScale(connection, device);
    }

    /****************************/
    /* LOCAL CACHE STORE        */
    /****************************/

    /**
     * Creates a `LocalCacheStore` over an already open local (`WSL`) connection.
     * Rejects remote (`WSR`) connections with null and an error log — fiscal
     * backup must never silently degrade onto a remote gateway.
     * Does not open or close the socket; caller owns connection lifecycle.
     * @param connection Open WebSocket connection (`WSL` required).
     * @returns Cache store, or null when connection is missing or not local.
     */
    WSCacheStore(connection: WSConnection | null): LocalCacheStore | null {
        if (!connection) {
            return null;
        }

        if (connection.contype != 'WSL') {
            console.error('[' + connection.contype + '] cache store requires a local websocket connection');
            return null;
        }

        return new LocalCacheStore(connection);
    }
}

